// ---------------------------------------------------------------------------
// scoreTier — qualitative presentation of the pleasant score.
//
// Real-drive history: the raw "Pleasant 28/100" number confused three of
// five test drives ("Pleasant 0" read as broken; a FOR-YOU card showing 28
// under a card showing 36 read as a contradiction). The number implies a
// precision the model hasn't earned yet — score-to-feeling calibration is
// still pending real-drive data (BACKLOG: scoring calibration).
//
// The fix is NOT hiding information (drive feedback explicitly asked for a
// comparable rating on every card). It's presenting the SAME scale as an
// honest word: tiers are comparable across cards at a glance, don't imply
// false precision, and never read as "broken." The exact numbers remain
// available in the expanded ScoreBreakdown for anyone who taps.
//
// Tier bands (tuned to current score distribution: collapse rules put the
// "pleasant beats fastest" bar at 8+ points and the score floor near 0):
//   ≥ 70  "Lovely"   — strong multi-signal evidence (water/trees/quiet)
//   55–69 "Calm"     — meaningfully nicer than the baseline
//   40–54 "Mixed"    — some nice stretches, some busy ones
//   < 40  "Busy"     — mostly arterial/highway character
// ---------------------------------------------------------------------------

import type { FeatureSummary } from "@/utils/scoring";

export type ScoreTier = "lovely" | "calm" | "mixed" | "busy";

export interface TierPresentation {
  tier: ScoreTier;
  /** Card-face label ("Lovely route"). */
  label: string;
  /** Tailwind classes for the chip. */
  chipClass: string;
}

const TIERS: Record<ScoreTier, Omit<TierPresentation, "tier">> = {
  lovely: {
    label: "Lovely route",
    chipClass: "bg-pleasant/15 text-primary font-semibold",
  },
  calm: {
    label: "Calm route",
    chipClass: "bg-primary/10 text-primary font-medium",
  },
  mixed: {
    label: "Mixed route",
    chipClass: "bg-muted text-foreground/70 font-medium",
  },
  busy: {
    label: "Busy route",
    chipClass: "bg-amber-500/15 text-amber-700 dark:text-amber-400 font-medium",
  },
};

export function tierForScore(pleasant: number): TierPresentation {
  const s = Math.max(0, pleasant);
  const tier: ScoreTier = s >= 70 ? "lovely" : s >= 55 ? "calm" : s >= 40 ? "mixed" : "busy";
  return { tier, ...TIERS[tier] };
}

/**
 * True when the score is built from (almost) no evidence — the cheap-path
 * placeholder features, or enrichment that genuinely found nothing. In
 * that state ANY rating overstates what we know; the honest chip is
 * "Limited data". (BACKLOG: '"Limited data" signal' — this is the explicit
 * badge follow-up.)
 */
export function isLimitedData(features: Partial<FeatureSummary> | undefined | null): boolean {
  if (!features) return true;
  const evidence =
    (features.parks ?? 0) +
    (features.landmarks ?? 0) +
    (features.cafes ?? 0) +
    (features.shops ?? 0) +
    (features.waterPct ?? 0) +
    (features.treeLinedKm ?? 0) +
    (features.residentialPct ?? 0) +
    (features.highwayPct ?? 0);
  return evidence <= 0;
}
